import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Positions from './components/Positions';
import ActiveList from './components/ActiveList';
import HiredList from './components/HiredList';
import ReleasedList from './components/ReleasedList';
import TerminatedList from './components/TerminatedList';
import Users from './components/Users';
import Metrics from './components/Metrics';
import PublicApplicationForm from './components/PublicApplicationForm';
import ChangePassword from './components/ChangePassword';
import Sidebar from './components/Sidebar';
import { auth } from './api';
import { LanguageProvider } from './i18n/LanguageContext';
import './index.css';

// Protected route wrapper that enforces password change
function ProtectedRoute({ user, onLogout, children }) {
  const location = useLocation();
  
  // If user must change password and not already on change-password page, redirect
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  
  return (
    <div className="app-container">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="main-content">
        {children}
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await auth.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const handlePasswordChanged = () => {
    // Update user object to reflect password was changed
    setUser(prev => ({ ...prev, must_change_password: false }));
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <Router>
      <Routes>
        {/* Public application form - no authentication required.
            Wrapped in LanguageProvider since this is the only page in the
            app with a language switcher (see client/src/i18n). */}
        <Route path="/apply" element={
          <LanguageProvider>
            <PublicApplicationForm />
          </LanguageProvider>
        } />
        
        <Route 
          path="/login" 
          element={
            isAuthenticated ? 
              <Navigate to="/" /> : 
              <Login onLogin={handleLogin} />
          } 
        />
        
        {isAuthenticated ? (
          <>
            {/* Password change route - standalone, no sidebar */}
            <Route 
              path="/change-password" 
              element={<ChangePassword onPasswordChanged={handlePasswordChanged} />} 
            />
            
            {/* All other routes wrapped in ProtectedRoute */}
            <Route path="/*" element={
              <ProtectedRoute user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Dashboard user={user} />} />
                  <Route path="/positions" element={<Positions user={user} />} />
                  <Route path="/active" element={<ActiveList user={user} />} />
                  <Route path="/hired" element={<HiredList user={user} />} />
                  <Route path="/released" element={<ReleasedList user={user} />} />
                  <Route path="/terminated" element={<TerminatedList user={user} />} />
                  <Route path="/users" element={<Users user={user} />} />
                  <Route path="/metrics" element={<Metrics user={user} />} />
                </Routes>
              </ProtectedRoute>
            } />
          </>
        ) : (
          <Route path="*" element={<Navigate to="/login" />} />
        )}
      </Routes>
    </Router>
  );
}

export default App;
